"use strict";

/***********************************************************************
 Sco2004Data
 ************************************************************************
 This class represents the data model of a SCORM 1.3 (2004) SCO.
 ***********************************************************************/

import {Assert} from "common/Assert";

export function Sco2004Data() {

    let EntryMap = new Map([
            ["lesson_id", 0],
            ["cmi.location", 1],
            ["cmi.completion_status", 2],
            ["cmi.success_status", 3],
            ["cmi.mode", 4],
            ["cmi.entry", 5],
            ["cmi.score.min", 6],
            ["cmi.score.max", 7],
            ["cmi.score.raw", 8],
            ["cmi.suspend_data", 9]
        ]),
        LessonModeMap = new Map([
            ["interactive", "i"],
            ["show", "s"],
            ["film", "f"]
        ]),
        LessonStatusMap = new Map([
            ["completed", "c"],
            ["incomplete", "i"],
            ["not attempted", "n"],
            ["unknown", ""]
        ]),
        SuccessStatusMap = new Map([
            ["passed", "p"],
            ["failed", "f"],
            ["unknown", ""]
        ]),
        scoDataArray = initializeArray(EntryMap.size);

    function initializeArray(size) {
        let initArray = [],
            i;

        for (i = 0; i < size; i++) {
            initArray.push("");
        }

        return initArray;
    }

    /***********************************************************************
     getKeyByValue
     ************************************************************************
     Finds the first key-name in a map matching to a given value.
     ***********************************************************************/
    function getKeyByValue(objMap, sValue) {

        Assert.checkArgument(objMap, "no objMap defined.");
        let result = "";
        objMap.forEach( (value, key ) => {
            if (value === sValue) {
                result = key;
            }
        });

        return result;
    }

    function importValue( key, value ){

        switch (key) {

            case "cmi.mode" :
                if (!LessonModeMap.has(value)) {
                    throw new Error("Unknown lesson mode value!");
                }
                return LessonModeMap.get(value);

            case "cmi.completion_status" :
                if (!LessonStatusMap.has(value)) {
                    throw new Error("Unknown completion status value!");
                }
                return LessonStatusMap.get(value);

            case "cmi.success_status" :
                if (!SuccessStatusMap.has(value)) {
                    throw new Error("Unknown success status value!");
                }
                return SuccessStatusMap.get(value);

            case "cmi.score.min":
            case "cmi.score.max":
            case "cmi.score.raw":
                return getScorm2004Score(value);
        }

        return value;
    }

    function getScorm2004Score( scoreValue ){

        if( scoreValue === "" ){
            return "";
        }

        let score = Number( scoreValue );
        if( isNaN(score) ){
            return "";
        }

        if( score > 1 ){
            score = Math.round( score ) / 100;
            return score.toString();
        }

        return scoreValue;
    }

    function ensureScorm2004Score(){

        const scoreMinIndex = EntryMap.get("cmi.score.min"),
              scoreMaxIndex = EntryMap.get("cmi.score.max"),
              scoreRawIndex = EntryMap.get("cmi.score.raw");

        scoDataArray[ scoreMinIndex ] = getScorm2004Score( scoDataArray[ scoreMinIndex ] );
        scoDataArray[ scoreMaxIndex ] = getScorm2004Score( scoDataArray[ scoreMaxIndex ] );
        scoDataArray[ scoreRawIndex ] = getScorm2004Score( scoDataArray[ scoreRawIndex ] );
    }

    function exportValue( scormModelId ){

        const entryIndex = EntryMap.get( scormModelId );
        let value = scoDataArray[entryIndex],
            keyName;

        switch ( scormModelId ) {

            case "cmi.mode":
                keyName = getKeyByValue(LessonModeMap, value);
                return (keyName.length > 0) ? keyName : value;

            case "cmi.completion_status":
                keyName = getKeyByValue(LessonStatusMap, value);
                return (keyName.length > 0) ? keyName : value;

            case "cmi.success_status":
                keyName = getKeyByValue(SuccessStatusMap, value);
                return (keyName.length > 0) ? keyName : value;
        }

        return value;
    }

    function getValue( scormModelId ) {

        Assert.checkArgument(scormModelId);

        if ( !EntryMap.has(scormModelId) ) {
            return "";
        }

        return exportValue(scormModelId);
    }


    // revealing public api
    return {

        getScoreModelId: () => "cmi.score.raw",
        getLessonLocationModelId: () => "cmi.location",
        getLessonStatusModelId: () => "cmi.completion_status",

        getId: () => { return getValue("lesson_id") },

        setValue : function (scormModelId, value) {

            Assert.checkArgument( scormModelId );

            if( value === "undefined" || !EntryMap.has(scormModelId) ) {
                return false;
            }

            try {
                const entryIndex = EntryMap.get(scormModelId);
                scoDataArray[entryIndex] = importValue( scormModelId, value );
            }
            catch( error ){
                return false;
            }

            return true;
        },

        getValue : getValue,

        isCompleted: function() {
            const scoLessonStatus = this.getValue("cmi.completion_status");
            return scoLessonStatus === "completed" || scoLessonStatus === "c";
        },

        isIncomplete: function() {
            const scoLessonStatus = this.getValue("cmi.completion_status");
            return scoLessonStatus === "incomplete" || scoLessonStatus === "i";
        },

        isFailed: function() {
            const scoLessonStatus = this.getValue("cmi.success_status");
            return scoLessonStatus === "failed" || scoLessonStatus === "f";
        },

        isPassed: function() {
            const scoLessonStatus = this.getValue("cmi.success_status");
            return scoLessonStatus === "passed" || scoLessonStatus === "p";
        },

        /**
         * exports all sco-values to a string.
         */
        exportToString: function ( sDelimiter ) {

            if (sDelimiter.length === 0) {
                sDelimiter = "$";
            }

            let sResult = "",
                i;

            for( i=0; i < scoDataArray.length; i++ ) {
                if (sResult.length > 0) {
                    sResult += sDelimiter;
                }
                sResult += scoDataArray[i];
            }

            return sResult;
        },

        /**
         * imports sco-values from a given string.
         */
        importFromString: function (sData, sDelimiter) {

            if (sData.length <= 0) {
                return false;
            }

            if (sDelimiter.length === 0) {
                sDelimiter = "$";
            }

            scoDataArray = sData.split( sDelimiter );

            ensureScorm2004Score();

            return scoDataArray.length === EntryMap.size;
        }
    };
}
